class CartSorter {
  static LAYOUTS = {
    drawer: {
      listSelector: 'tbody[role="rowgroup"]',
      itemIdPattern: /^CartDrawer-Item-(\d+)$/,
    },
    pageTable: {
      listSelector: 'tbody',
      itemIdPattern: /^CartItem-(\d+)$/,
    },
    pageCustom: {
      listSelector: '.cart-table__items-list',
      itemIdPattern: /^CartItem-(\d+)$/,
    },
  };

  static sortSectionHtml(sectionHtml, items, layoutKey) {
    if (!sectionHtml || !items?.length) return sectionHtml;

    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const root = doc.body.firstElementChild || doc.body;
    this.sortItemsInRoot(root, items, layoutKey);
    return root.outerHTML;
  }

  static sortItemsInElement(element, items, layoutKey) {
    if (!element || !items?.length) return;
    this.sortItemsInRoot(element, items, layoutKey);
  }

  static sortItemsInRoot(root, items, layoutKey) {
    const layout = this.LAYOUTS[layoutKey] || this.LAYOUTS.pageTable;
    const listContainer = root.querySelector(layout.listSelector);
    if (!listContainer) return;

    const itemElements = this.collectItemElements(listContainer, layout.itemIdPattern);
    if (!itemElements.size) return;

    const sortedLineNumbers = this.computeSortedLineOrder(items);
    const sortedHtml = this.buildSortedListHtml(itemElements, sortedLineNumbers);

    listContainer.innerHTML = sortedHtml;
  }

  static collectItemElements(listContainer, itemIdPattern) {
    const itemElements = new Map();

    for (const child of listContainer.children) {
      const match = child.id?.match(itemIdPattern);
      if (match) itemElements.set(parseInt(match[1], 10), child.outerHTML);
    }

    return itemElements;
  }

  static buildSortedListHtml(itemElements, sortedLineNumbers) {
    const used = new Set();
    const parts = [];

    for (const lineNumber of sortedLineNumbers) {
      const html = itemElements.get(lineNumber);
      if (!html) continue;
      parts.push(html);
      used.add(lineNumber);
    }

    for (const [lineNumber, html] of itemElements) {
      if (used.has(lineNumber)) continue;
      parts.push(html);
    }

    return parts.join('');
  }

  static computeSortedLineOrder(items) {
    if (!items?.length) return [];

    const indexedItems = items.map((item, index) => ({ item, index }));
    const addonEntries = [];
    const mainEntries = [];

    indexedItems.forEach((entry) => {
      if (this.isAddonItem(entry.item, entry.index, items)) {
        addonEntries.push(entry);
      } else {
        mainEntries.push(entry);
      }
    });

    const addonsByParent = new Map();

    addonEntries.forEach((entry) => {
      const parentIndex = this.resolveParentIndex(entry.item, entry.index, items, mainEntries);
      if (!addonsByParent.has(parentIndex)) addonsByParent.set(parentIndex, []);
      addonsByParent.get(parentIndex).push(entry);
    });

    const sortedLineNumbers = [];

    mainEntries.forEach((mainEntry) => {
      sortedLineNumbers.push(mainEntry.index + 1);
      const addons = addonsByParent.get(mainEntry.index) || [];
      addons.forEach((addonEntry) => sortedLineNumbers.push(addonEntry.index + 1));
    });

    const orphanAddons = addonsByParent.get(null) || [];
    orphanAddons.forEach((addonEntry) => sortedLineNumbers.push(addonEntry.index + 1));

    return sortedLineNumbers;
  }

  static isAddonItem(item, index, items) {
    if (this.hasParentRelationship(item)) return true;

    const originalLinePrice = this.getOriginalLinePrice(item);
    const finalLinePrice = this.getFinalLinePrice(item);

    if (originalLinePrice > 0 && finalLinePrice === 0) return true;

    const discountTotal = this.getLineDiscountTotal(item);
    if (originalLinePrice > 0 && discountTotal >= originalLinePrice) return true;

    if (this.getFinalUnitPrice(item) === 0 && this.getOriginalUnitPrice(item) > 0) return true;

    return false;
  }

  static hasParentRelationship(item) {
    if (item.parent_id) return true;
    if (item.parent_relationship?.parent) return true;
    if (item.parent_relationship?.parent_key) return true;
    return false;
  }

  static resolveParentIndex(addonItem, addonIndex, items, mainEntries) {
    const parentKey = addonItem.parent_relationship?.parent_key || addonItem.parent_relationship?.parent?.key;
    if (parentKey) {
      const parentIndex = items.findIndex((item) => item.key === parentKey);
      if (parentIndex >= 0) return parentIndex;
    }

    const parentVariantId = addonItem.parent_id || addonItem.parent_relationship?.parent?.variant_id;
    if (parentVariantId) {
      const parentIndex = items.findIndex(
        (item, index) =>
          index !== addonIndex &&
          !this.isAddonItem(item, index, items) &&
          (item.variant_id === parentVariantId || item.id === parentVariantId)
      );
      if (parentIndex >= 0) return parentIndex;
    }

    const propertyParentIndex = this.resolveParentFromProperties(addonItem, items, mainEntries);
    if (propertyParentIndex !== null) return propertyParentIndex;

    const sharedDiscountParentIndex = this.resolveParentFromSharedDiscounts(
      addonItem,
      addonIndex,
      items,
      mainEntries
    );
    if (sharedDiscountParentIndex !== null) return sharedDiscountParentIndex;

    return this.resolvePrecedingMainIndex(addonIndex, items);
  }

  static resolveParentFromProperties(addonItem, items, mainEntries) {
    const properties = addonItem.properties;
    if (!properties || typeof properties !== 'object') return null;

    const propertyValues = Object.entries(properties).flatMap(([key, value]) => {
      if (!value || key.startsWith('_')) return [];
      return [String(value)];
    });

    const hiddenPropertyValues = Object.entries(properties).flatMap(([key, value]) => {
      if (!value || !key.startsWith('_')) return [];
      const normalizedKey = key.slice(1).toLowerCase();
      if (!/(parent|bundle|group|line|main)/.test(normalizedKey)) return [];
      return [String(value)];
    });

    const candidates = [...propertyValues, ...hiddenPropertyValues];
    if (!candidates.length) return null;

    for (const mainEntry of mainEntries) {
      const mainItem = mainEntry.item;
      const mainIdentifiers = [
        mainItem.key,
        String(mainItem.variant_id),
        String(mainItem.id),
        String(mainItem.product_id),
      ].filter(Boolean);

      if (candidates.some((candidate) => mainIdentifiers.includes(candidate))) {
        return mainEntry.index;
      }
    }

    return null;
  }

  static resolveParentFromSharedDiscounts(addonItem, addonIndex, items, mainEntries) {
    const addonDiscountKeys = this.getDiscountApplicationKeys(addonItem);
    if (!addonDiscountKeys.size) return null;

    for (const mainEntry of mainEntries) {
      const mainDiscountKeys = this.getDiscountApplicationKeys(mainEntry.item);
      for (const key of addonDiscountKeys) {
        if (mainDiscountKeys.has(key)) return mainEntry.index;
      }
    }

    for (let index = addonIndex - 1; index >= 0; index -= 1) {
      if (!this.isAddonItem(items[index], index, items)) return index;
    }

    return null;
  }

  static resolvePrecedingMainIndex(addonIndex, items) {
    for (let index = addonIndex - 1; index >= 0; index -= 1) {
      if (!this.isAddonItem(items[index], index, items)) return index;
    }
    return null;
  }

  static getDiscountApplicationKeys(item) {
    const keys = new Set();
    const allocations = [
      ...(item.line_level_discount_allocations || []),
      ...(item.discount_allocations || []),
    ];

    allocations.forEach((allocation) => {
      const application = allocation.discount_application || allocation;
      const key = application.key || application.title;
      if (key) keys.add(String(key));
    });

    (item.discounts || []).forEach((discount) => {
      const key = discount.key || discount.title;
      if (key) keys.add(String(key));
    });

    return keys;
  }

  static getLineDiscountTotal(item) {
    if (typeof item.total_discount === 'number') return item.total_discount;

    const allocations = [
      ...(item.line_level_discount_allocations || []),
      ...(item.discount_allocations || []),
      ...(item.discounts || []),
    ];

    return allocations.reduce((total, allocation) => total + (allocation.amount || 0), 0);
  }

  static getOriginalLinePrice(item) {
    if (typeof item.original_line_price === 'number') return item.original_line_price;
    return this.getOriginalUnitPrice(item) * (item.quantity || 1);
  }

  static getFinalLinePrice(item) {
    if (typeof item.final_line_price === 'number') return item.final_line_price;
    if (typeof item.line_price === 'number') return item.line_price;
    return this.getFinalUnitPrice(item) * (item.quantity || 1);
  }

  static getOriginalUnitPrice(item) {
    return item.original_price ?? item.price ?? 0;
  }

  static getFinalUnitPrice(item) {
    return item.final_price ?? item.discounted_price ?? item.price ?? 0;
  }

  static resolveLayoutFromElement(element) {
    if (element?.tagName === 'CART-DRAWER-ITEMS') return 'drawer';

    const sectionType = document.getElementById('main-cart-items')?.dataset.sectionType;
    if (sectionType === 'main-cart-page') return 'pageCustom';

    return 'pageTable';
  }

  static applyInitialSort(element) {
    if (!element || element.classList.contains('is-empty')) return;

    fetch(`${window.routes?.cart_url || '/cart'}.js`)
      .then((response) => response.json())
      .then((cartData) => {
        if (!cartData.items?.length) return;
        const layout = this.resolveLayoutFromElement(element);
        this.sortItemsInElement(element, cartData.items, layout);
        element.classList.add('cart-items--sorted');
      })
      .catch(() => {});
  }
}

window.CartSorter = CartSorter;

if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (event.source === 'cart-items' || event.source === 'product-form' || !event.cartData?.items?.length) {
      return;
    }

    requestAnimationFrame(() => {
      document.querySelectorAll('cart-drawer-items, cart-items').forEach((element) => {
        if (element.classList.contains('is-empty')) return;

        const layout = CartSorter.resolveLayoutFromElement(element);
        CartSorter.sortItemsInElement(element, event.cartData.items, layout);
        element.classList.add('cart-items--sorted');
      });
    });
  });
}
